import { useMemo } from 'react';
import { string, StringSchema } from 'yup';

import { usePolicies } from '../queries/usePolicies';
import { PolicyId } from '../types';

/**
 * Hook that returns a Yup string validation schema for policy names.
 * Validates that the name is:
 * - Required and non-empty
 * - Trimmed
 * - Unique across all policies (case-insensitive)
 * - Only validates uniqueness after policies data is loaded
 *
 * @param currentPolicyId - Optional policy ID to exclude from uniqueness check (for edit mode)
 * @returns Yup string schema for policy name validation
 *
 */
export function useNameValidation(
  currentPolicyId?: PolicyId
): StringSchema<string> {
  const policiesQuery = usePolicies();
  const { data, isLoading } = policiesQuery;

  return useMemo(() => {
    const existingPolicies = data?.policies || [];

    return string()
      .required('Policy name is required')
      .trim()
      .min(1, 'Policy name is required')
      .test('unique', function validate(value) {
        if (isLoading) {
          return this.createError({
            message: 'Still loading policies. Please wait before submitting.',
          });
        }

        if (
          !existingPolicies.every(
            (p) =>
              p.Id === currentPolicyId ||
              p.Name.toLowerCase() !== value?.toLowerCase()
          )
        ) {
          return this.createError({ message: 'Policy name must be unique' });
        }

        return true;
      }) as StringSchema<string>;
  }, [data, isLoading, currentPolicyId]);
}
